import Accordion from "@/components/Accordion";
import { Toggle } from "@/components/Toggle";
import { StyleConfigModel } from "@/pages/Messaging/types";
import React from "react";
import { ChatWindow } from "../../types";
import DesignerActionLink from "../../DesignerActionLink";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
}

const openAnimationOptions = [
  { value: "slideUp", label: "Slide up" },
  { value: "fade", label: "Fade" },
  { value: "pop", label: "Scale" },
  { value: "none", label: "None" }
];

const bubbleEntranceOptions = [
  { value: "fade", label: "Fade" },
  { value: "pop", label: "Pop" },
  { value: "none", label: "None" }
];

const shadowIntensityOptions = [
  { value: "none", label: "None" },
  { value: "subtle", label: "Subtle" },
  { value: "medium", label: "Medium" },
  { value: "strong", label: "Strong" }
];

const typingIndicatorOptions = [
  { value: "dots", label: "Dots" },
  { value: "wave", label: "Wave" },
  { value: "thinking", label: "Thinking" },
  { value: "none", label: "None" }
];

const launcherAttentionOptions = [
  { value: "none", label: "None" },
  { value: "shake", label: "Shake" },
  { value: "bounce", label: "Bounce" },
  { value: "ring", label: "Ring" }
];

const EFFECTS_DEFAULTS: Partial<StyleConfigModel> = {
  openAnimation: "slideUp",
  bubbleEntranceAnimation: "fade",
  shadowIntensity: "subtle",
  glassmorphism: false,
  launcherPulse: false,
  messageReceivedFlash: false,
  typingIndicatorStyle: "dots",
  launcherAttentionMode: "none"
};

// WCAG-friendly: minimal motion, no continuous animation, no glassmorphism.
const EFFECTS_RECOMMENDED: Partial<StyleConfigModel> = {
  openAnimation: "fade",
  bubbleEntranceAnimation: "fade",
  shadowIntensity: "subtle",
  glassmorphism: false,
  launcherPulse: false,
  messageReceivedFlash: false,
  typingIndicatorStyle: "dots",
  launcherAttentionMode: "none"
};

export type SimulateEffect =
  | "openAnimation"
  | "bubbleEntrance"
  | "launcherPulse"
  | "shadowFlash"
  | "glassmorphism"
  | "messageReceivedFlash"
  | "typingIndicator"
  | "launcherAttention";

export interface SimulateEvent {
  effect: SimulateEffect;
  /** Optional sub-variant — e.g. attention mode, typing style */
  variant?: string;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function dispatchSimulate(detail: SimulateEvent) {
  window.dispatchEvent(new CustomEvent("chatDesignerSimulate", { detail }));
}

function PreviewButton({
  onClick,
  disabledReason
}: {
  onClick: () => void;
  disabledReason?: string;
}) {
  const [playing, setPlaying] = React.useState(false);
  const reduced = prefersReducedMotion();
  return (
    <div className="flex items-center gap-2">
      <DesignerActionLink
        ariaLabel="Preview this effect"
        title={disabledReason ?? "Preview this effect in the device frame"}
        disabled={!!disabledReason || playing}
        onClick={() => {
          if (playing) return;
          setPlaying(true);
          onClick();
          window.setTimeout(() => setPlaying(false), 3500);
        }}
      >
        {playing ? "Playing…" : "▶ Preview"}
      </DesignerActionLink>
      {reduced && !disabledReason && (
        <span className="text-[11px] text-slate-400">
          (reduced-motion: shown statically)
        </span>
      )}
    </div>
  );
}

export function Effects(props: Props) {
  const workingStyleConfig = props.workingChatWindow.styleConfig || {};

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      styleConfig: {
        ...workingStyleConfig,
        ...updates
      }
    });
  };

  const pillRow = (
    value: string,
    options: { value: string; label: string }[],
    onSelect: (value: string) => void,
    groupLabel: string
  ) => (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={groupLabel}>
      {options.map((opt) => {
        const isSelected = value === opt.value;
        return (
          <button
            type="button"
            role="radio"
            aria-checked={isSelected}
            key={opt.value}
            className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
              isSelected
                ? "border-primary bg-primary-bg text-primary-active"
                : "border-slate-300 bg-white text-slate-600"
            }`}
            onClick={() => onSelect(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  const wcagEnabled = workingStyleConfig.enforceWcagAa ?? true;
  const glass = workingStyleConfig.glassmorphism ?? false;
  const pulse = workingStyleConfig.launcherPulse ?? false;
  const messageReceivedFlash = workingStyleConfig.messageReceivedFlash ?? false;
  const typingStyle = workingStyleConfig.typingIndicatorStyle ?? "dots";
  const attentionMode = workingStyleConfig.launcherAttentionMode ?? "none";

  return (
    <Accordion.Item eventKey="effects" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Effects</Accordion.Title>
        <Accordion.SubTitle>
          Subtle animations and finishes for the chat window and launcher
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="space-y-5">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                if (
                  !window.confirm(
                    "Apply recommended (WCAG + UX-friendly) effect values? Existing effect settings will be replaced."
                  )
                )
                  return;
                updateStyleConfig(EFFECTS_RECOMMENDED);
              }}
              aria-label="Apply recommended effect values"
              className="text-xs hover:underline rounded-sm min-h-[24px] px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
              style={{ color: "var(--color-primary)" }}
            >
              Recommended
            </button>
            <button
              type="button"
              onClick={() => {
                if (!window.confirm("Reset effect fields to their defaults?")) return;
                updateStyleConfig(EFFECTS_DEFAULTS);
              }}
              aria-label="Reset effects section to defaults"
              className="text-xs hover:underline rounded-sm min-h-[24px] px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
              style={{ color: "var(--color-primary)" }}
            >
              Reset section
            </button>
          </div>
          {/* Open animation */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-medium">Open Animation</div>
              <PreviewButton onClick={() => dispatchSimulate({ effect: "openAnimation" })} />
            </div>
            <div className="text-xs text-slate-400 mb-2">
              How the chat window animates into view when the visitor opens it
            </div>
            {pillRow(
              workingStyleConfig.openAnimation ?? "slideUp",
              openAnimationOptions,
              (value) => updateStyleConfig({ openAnimation: value }),
              "Open Animation"
            )}
          </div>

          {/* Bubble entrance */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-medium">Bubble Entrance</div>
              <PreviewButton onClick={() => dispatchSimulate({ effect: "bubbleEntrance" })} />
            </div>
            <div className="text-xs text-slate-400 mb-2">
              Animation applied to each incoming message bubble as it appears
            </div>
            {pillRow(
              workingStyleConfig.bubbleEntranceAnimation ?? "fade",
              bubbleEntranceOptions,
              (value) => updateStyleConfig({ bubbleEntranceAnimation: value }),
              "Bubble Entrance"
            )}
          </div>

          {/* Shadow intensity */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-medium">Shadow Intensity</div>
              <PreviewButton onClick={() => dispatchSimulate({ effect: "shadowFlash" })} />
            </div>
            <div className="text-xs text-slate-400 mb-2">
              Depth of the drop shadow under the window and launcher
            </div>
            {pillRow(
              workingStyleConfig.shadowIntensity ?? "subtle",
              shadowIntensityOptions,
              (value) => updateStyleConfig({ shadowIntensity: value }),
              "Shadow Intensity"
            )}
          </div>

          <div className="border-t border-slate-200" />

          {/* Glassmorphism */}
          <div className="flex justify-between items-center">
            <div className="pr-6">
              <div className="text-sm font-normal">Frosted header</div>
              <div className="text-xs text-slate-400">
                Adds a frosted glass effect to the header. May reduce readability — check contrast.
              </div>
            </div>
            <div className="flex items-center gap-3">
              <PreviewButton onClick={() => dispatchSimulate({ effect: "glassmorphism" })} />
              <Toggle
                toggled={workingStyleConfig.glassmorphism ?? false}
                changeToggle={() =>
                  updateStyleConfig({ glassmorphism: !(workingStyleConfig.glassmorphism ?? false) })
                }
              />
            </div>
          </div>
          {wcagEnabled && glass && (
            <div
              role="note"
              className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-2"
            >
              Frosted backgrounds can reduce text contrast. Verify header text
              meets 4.5:1 against the blurred content behind it.
            </div>
          )}

          {/* Launcher pulse */}
          <div className="flex justify-between items-center">
            <div className="pr-6">
              <div className="text-sm font-normal">Launcher pulse</div>
              <div className="text-xs text-slate-400">
                Subtle 2-second pulse on the launcher; disabled once the visitor opens chat.
              </div>
            </div>
            <div className="flex items-center gap-3">
              <PreviewButton onClick={() => dispatchSimulate({ effect: "launcherPulse" })} />
              <Toggle
                toggled={workingStyleConfig.launcherPulse ?? false}
                changeToggle={() =>
                  updateStyleConfig({ launcherPulse: !(workingStyleConfig.launcherPulse ?? false) })
                }
              />
            </div>
          </div>
          {wcagEnabled && pulse && (
            <div
              role="note"
              className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-2"
            >
              Continuous animation may violate WCAG 2.2.2 (Pause, Stop, Hide)
              unless users can disable it. Velaro automatically respects users'
              reduced-motion preference.
            </div>
          )}

          <div className="border-t border-slate-200" />

          {/* Message received flash */}
          <div className="flex justify-between items-center">
            <div className="pr-6">
              <div className="text-sm font-normal">Agent message flash</div>
              <div className="text-xs text-slate-400">
                Briefly flashes the agent bubble border in your primary color when a new
                agent message arrives (~200ms). Subtle attention grabber.
              </div>
            </div>
            <div className="flex items-center gap-3">
              <PreviewButton
                onClick={() => dispatchSimulate({ effect: "messageReceivedFlash" })}
              />
              <Toggle
                toggled={messageReceivedFlash}
                changeToggle={() =>
                  updateStyleConfig({ messageReceivedFlash: !messageReceivedFlash })
                }
              />
            </div>
          </div>

          {/* Typing indicator style */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-medium">Typing Indicator</div>
              <PreviewButton
                onClick={() =>
                  dispatchSimulate({ effect: "typingIndicator", variant: typingStyle })
                }
              />
            </div>
            <div className="text-xs text-slate-400 mb-2">
              Visual style of the indicator shown while an agent is typing.
            </div>
            {pillRow(
              typingStyle,
              typingIndicatorOptions,
              (value) => updateStyleConfig({ typingIndicatorStyle: value }),
              "Typing Indicator"
            )}
          </div>

          {/* Launcher attention mode */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-medium">Launcher Attention</div>
              <PreviewButton
                onClick={() =>
                  dispatchSimulate({ effect: "launcherAttention", variant: attentionMode })
                }
                disabledReason={attentionMode === "none" ? "Pick a mode to preview" : undefined}
              />
            </div>
            <div className="text-xs text-slate-400 mb-2">
              One-time animation when the widget loads (plays for ~3 seconds, then stops).
              Always suppressed when the visitor has reduced-motion enabled.
            </div>
            {pillRow(
              attentionMode,
              launcherAttentionOptions,
              (value) => updateStyleConfig({ launcherAttentionMode: value }),
              "Launcher Attention"
            )}
            {attentionMode === "shake" || attentionMode === "bounce" ? (
              <div
                role="note"
                className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-2 mt-2"
              >
                Repeated shake or bounce animations can cause vestibular discomfort
                (WCAG 2.3.3). Use sparingly and only as a one-time attention cue.
              </div>
            ) : null}
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
